import {
  useComplaintCategoryByUtility,
  useComplaintSubCategoryByCategory,
  useCreateComplaintConfig,
  useFrequency,
  useSeverityLevel,
  useUpdatecomplaintConfig,
  useUtilityServices,
} from "@features/cx/settings/hooks";
import { ComplaintTypeFormProps } from "@features/cx/settings/types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { ContextualHelp } from "@shared/ui/contextual-help";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ScrollArea } from "@shared/ui/scroll-area";
import { AlertTriangle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export function ComplaintTypeForm({
  complaintType,
  open,
  onClose,
  onSave,
}: ComplaintTypeFormProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const [formData, setFormData] = useState({
    name: complaintType?.name,
    code: complaintType?.configurationCode,
    description: complaintType?.longDescription,
    utility_service: complaintType?.productCode,
    category: complaintType?.supportRequestType,
    subcategory: complaintType?.supportRequestSubtype,
    severity: complaintType?.extraData.severityLevel,
    responseTime: complaintType?.responseTime,
    resolutionTime: complaintType?.resolutionTime,
    acknowledgmentTime: complaintType?.acknowlegmentTime,
    // updateFrequency: complaintType?.consumerUpdateFrequency,
    requiresApproval: complaintType?.requiresApproval,
  });
  const [utilityService, setUtilityService] = useState<any>("");
  const [codeList, setCodeList] = useState<any>("");
  const [selectedUtility, setSelectedUtility] = useState(
    complaintType?.productCode || ""
  );
  const { data: utilities } = useUtilityServices();
  const { data: complaintCategories = [], refetch: refetchCategories } =
    useComplaintCategoryByUtility(utilityService);
  const { data: ComplaintSubCategories = [], refetch: refetchSubCategories } =
    useComplaintSubCategoryByCategory(codeList);
  const { data: severityData } = useSeverityLevel();
  const { data: frequencyData } = useFrequency();
  const createComplaintConfig = useCreateComplaintConfig();
  const updateConfig = useUpdatecomplaintConfig(complaintType?.id);

  // Mock categories check
  const categoriesExist = true;

  useEffect(() => {
    if (open) {
      refetchCategories();
      refetchSubCategories();
    }
  }, [open, refetchCategories, refetchSubCategories]);

  useEffect(() => {
    const operation = complaintType ? "EDIT" : "CREATE";
    switch (operation) {
      case "EDIT":
        setFormData({
          name: complaintType.name || "",
          code: complaintType.configurationCode || "",
          description: complaintType.longDescription || "",
          utility_service: complaintType.productCode || [],
          category: complaintType.supportRequestType || "",
          subcategory: complaintType.supportRequestSubtype || "",
          severity: complaintType.extraData.severityLevel,
          responseTime: complaintType.extraData.responseTime || "",
          resolutionTime: complaintType.extraData.resolutionTime || "",
          acknowledgmentTime: complaintType.extraData.acknowlegmentTime || "",
          // updateFrequency:
          //   complaintType.extraData.consumerUpdateFrequency || "",
          requiresApproval: complaintType.extraData.requiresApproval || "",
        });
        setSelectedUtility(complaintType.productCode || "");
        setUtilityService(complaintType.productCode || "");
        setCodeList(complaintType.supportRequestType || "");
        break;
    }
  }, [complaintType]);

  // Memoized options
  const getUtilityOptions = useMemo(() => {
    if (!utilities || !Array.isArray(utilities)) {
      return [];
    }
    return utilities
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilities]);

  // Memoized options for dropdowns
  const getComplaintCategoryOptions = useMemo(() => {
    if (!complaintCategories || !Array.isArray(complaintCategories)) {
      return [];
    }
    return complaintCategories.map((category) => {
      return {
        value: category.code,
        label: category.name,
      };
    });
  }, [complaintCategories]);

  const getComplaintSubCategoryOptions = useMemo(() => {
    if (!ComplaintSubCategories || !Array.isArray(ComplaintSubCategories)) {
      return [];
    }
    return ComplaintSubCategories.map((subCategory) => {
      return {
        value: subCategory.code,
        label: subCategory.name,
      };
    });
  }, [ComplaintSubCategories]);

  const getSeverityOptions = useMemo(() => {
    if (!severityData || !Array.isArray(severityData)) {
      return [];
    }
    return severityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [severityData]);

  const getUpdateFrequencyOptions = useMemo(() => {
    if (!frequencyData || !Array.isArray(frequencyData)) {
      return [];
    }
    return frequencyData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [frequencyData]);

  // Define form fields with grouping
  const formFields: FormField[] = [
    // Basic Information Group
    {
      name: "name",
      type: "text",
      label: "Complaint Type Name",
      placeholder: "e.g., Water Pressure Issue",
      required: true,
      group: "Basic Information",
      className: "col-span-1",
    },
    {
      name: "code",
      type: "text",
      label: "Type Code",
      placeholder: "e.g., WP-PRES-001",
      required: true,
      group: "Basic Information",
      className: "col-span-1",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Detailed complaint definition for staff reference",
      rows: 3,
      group: "Basic Information",
      className: "col-span-2",
    },

    // Complaint Classification Group
    {
      name: "utility_service",
      label: "Utility Services",
      type: "multi-select",
      required: true,
      options: getUtilityOptions,
      placeholder: "Select utility services...",
      fullWidth: true,
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      group: "Complaint Classification",
      className: "col-span-2",
      classes: {
        // container: "w-full mb-4",
        // label: "block text-sm font-medium text-gray-700 mb-2",
        // select: `w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200`,
        // error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "category",
      type: "select",
      label: "Complaint Category",
      placeholder: "Select category",
      required: true,
      group: "Complaint Classification",
      options: getComplaintCategoryOptions,
      className: "col-span-1",
    },
    {
      name: "subcategory",
      type: "select",
      label: "Complaint Subcategory",
      placeholder: "Select subcategory",
      required: true,
      group: "Complaint Classification",
      options: getComplaintSubCategoryOptions,
      className: "col-span-1",
    },
    {
      name: "severity",
      type: "select",
      label: "Priority Level",
      placeholder: "Select priority",
      required: true,
      group: "Complaint Classification",
      options: getSeverityOptions,
      className: "col-span-2",
    },

    // Resolution Configuration Group
    {
      name: "acknowledgmentTime",
      type: "number",
      label: "Acknowledgment Time (hours)",
      placeholder: "2",
      required: true,
      group: "Resolution Configuration",
      className: "col-span-1",
      // min: 0,
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (hours)",
      placeholder: "8",
      required: true,
      group: "Resolution Configuration",
      className: "col-span-1",
      // min: 0,
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (hours)",
      placeholder: "48",
      required: true,
      group: "Resolution Configuration",
      className: "col-span-1",
      // min: 0,
    },
    // {
    //   name: "updateFrequency",
    //   type: "select",
    //   label: "Customer Update Frequency",
    //   placeholder: "Select frequency",
    //   required: true,
    //   group: "Resolution Configuration",
    //   options: getUpdateFrequencyOptions,
    //   className: "col-span-2",
    // },

    // Closure Requirements Group
    {
      name: "requiresApproval",
      type: "switch",
      label: "Requires Approval",
      group: "Closure Requirements",
      helperText: "Whether this reason requires manager approval",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    switch (fieldName) {
      case "utility_service":
        setUtilityService(value);
        break;
      case "category":
        setCodeList(value);
        break;
    }
  };

  const handleSubmit = (data: any) => {
    const operation = complaintType ? "UPDATE" : "ADD";

    switch (operation) {
      case "UPDATE": {
        // Update Complaint Configuration
        const updatePayload = {
          remote_utility_id: Number(remoteUtilityId),
          long_description: formData.description,
          product_code: formData.utility_service,
          support_request_type: formData.category,
          support_request_subtype: formData.subcategory,
          request_type: "Complaint",
          extra_data: {
            severity_level: formData.severity,
            response_time: formData.responseTime,
            resolution_time: formData.resolutionTime,
            acknowlegment_time: formData.acknowledgmentTime,
            // consumer_update_frequency: formData.updateFrequency,
            requires_approval: formData.requiresApproval || false,
          },
        };
        updateConfig.mutateAsync(updatePayload);
        onClose();
        handleReset();
        break;
      }

      case "ADD": {
        // Create Complaint Configuration
        const payload = {
          remote_utility_id: Number(remoteUtilityId),
          name: data.name,
          configuration_code: data.code,
          long_description: data.description,
          product_code: data.utility_service,
          support_request_type: data.category,
          support_request_subtype: data.subcategory,
          request_type: "Complaint",
          extra_data: {
            severity_level: parseInt(data.severity),
            response_time: data.responseTime,
            resolution_time: data.resolutionTime,
            acknowlegment_time: data.acknowledgmentTime,
            field_force_required: data.fieldWorkRequired || false,
            // consumer_update_frequency: parseInt(data.updateFrequency),
            requires_approval: data.requiresApproval || false,
          },
        };
        createComplaintConfig.mutateAsync(payload);
        onClose();
        handleReset();
        break;
      }
    }
  };

  const handleReset = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {complaintType
              ? "Edit Complaint Type"
              : "Create New Complaint Type"}
            <ContextualHelp
              title="Complaint Type Configuration"
              content="Configure specific complaint types with detailed classification, severity levels, and resolution procedures. Complaint types define how different customer issues are categorized, prioritized, and resolved."
            />
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-1">
          <div className="space-y-6 pr-4">
            {/* Prerequisites Check */}
            {!categoriesExist && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-orange-800">
                        Missing Categories Alert
                      </h4>
                      <p className="text-sm text-orange-700 mt-1">
                        Some required complaint categories are missing. Please
                        create them first before configuring complaint types.
                      </p>
                      <Button variant="outline" size="sm" className="mt-2">
                        Create Missing Categories
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dynamic Form with Grid Layout */}
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                resetButtonText: "Cancel",
                submitButtonText: complaintType
                  ? "Update Complaint Type"
                  : "Create Complaint Type",
                resetOnSubmit: false,
                spacing: "comfortable",
                classes: {
                  fieldGroup:
                    "bg-white rounded-lg p-6 border-none",
                  button: "px-6 py-2 rounded-md font-medium transition-colors",
                  submitButton:
                    "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
                  resetButton:
                    "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-sm text-gray-500 mt-1 flex",
                  checkbox:
                    "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500",

                },
              }}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
